import { ChevronDown, Loader2 } from 'lucide-react';
import { useRef, useState } from 'react';

import type { RxFormApi } from '../../model/types';

import { usePatientEligibilitiesQuery } from '@/entities/eligibility';
import { useDispenseUnitsQuery, usePatientFormularyQuery } from '@/entities/rx';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/shadcn/select';
import { FormField } from '@/shared/ui/primitives';

import { deriveFormularyDisplayState } from '../../model/formularyDisplayPolicy';
import { buildDispenseUnitOptions } from '../../model/dispenseUnitOptions';
import { useAlternativeSwitcher } from '../../model/useAlternativeSwitcher';
import { usePatientFormularyParams } from '../../model/usePatientFormularyParams';
import { PlanAlternativesDialog } from '../BenefitsCoordinationPanel';
import { FormularyDetailDialog } from '../FormularyDetailDialog';
import { FormularyStatusBadge } from '../FormularyStatusBadge';
import { MedicationMetaBadges } from '../MedicationMetaBadges';
import {
  DiagnosisSelector,
  IngredientsEditor,
  MedicationSelector,
  PharmacySelector,
} from '../selectors';

interface Props {
  form: RxFormApi;
  userId: string;
  diagnosisRequiredForInsuranceApproval?: boolean;
  /**
   * Optional override pinning the formulary lookup to a specific
   * ``PatientEligibilityId``. ``null`` means "auto" (server picks the
   * first active coverage that covers the prescription's channel).
   *
   * When the host doesn't pass ``onFormularyEligibilityOverrideChange``
   * (e.g. the rxchange / refill modify wizard which has no place for
   * the picker), the dropdown is hidden entirely.
   */
  formularyEligibilityOverride?: number | null;
  onFormularyEligibilityOverrideChange?: (value: number | null) => void;
}

const FORMULARY_ELIGIBILITY_AUTO_VALUE = 'auto';

export function DetailsStep({
  form,
  userId,
  diagnosisRequiredForInsuranceApproval = false,
  formularyEligibilityOverride = null,
  onFormularyEligibilityOverrideChange,
}: Props) {
  const isCoded = form.state.values.kind === 'coded';
  const dispenseUnitsQuery = useDispenseUnitsQuery();
  const eligibilitiesQuery = usePatientEligibilitiesQuery(userId);
  const formularyParams = usePatientFormularyParams(form, formularyEligibilityOverride);
  const formularyQuery = usePatientFormularyQuery({ userId, params: formularyParams });

  const [alternativesOpen, setAlternativesOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const lastAutoDispenseUnitIdRef = useRef<string | null>(null);
  const { selectAlternative, isSwitching } = useAlternativeSwitcher({
    form,
    lastAutoDispenseUnitIdRef,
  });

  const selectedMedication =
    form.state.values.kind === 'coded' ? form.state.values.selectedMedication : null;
  const formulary = formularyQuery.data?.formulary;
  const display = deriveFormularyDisplayState({ formulary, medication: selectedMedication });
  const alternativeCount =
    display.payerAlternatives.length + display.therapeuticAlternatives.length;
  const hasSelectedMedication =
    form.state.values.kind === 'coded' && Boolean(form.state.values.selectedMedication);
  const isFormularyFetching = formularyQuery.isFetching;
  const showAlternativesButton = hasSelectedMedication && alternativeCount > 0;
  const dispenseUnitOptions = buildDispenseUnitOptions(dispenseUnitsQuery.data);
  const dispenseUnitDescription = dispenseUnitsQuery.isPending
    ? 'Loading units…'
    : dispenseUnitsQuery.isError
      ? 'Could not load units.'
      : dispenseUnitOptions.length === 0
        ? 'No units available.'
        : undefined;
  const dispenseUnitRetryButton = dispenseUnitsQuery.isError ? (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      onClick={() => void dispenseUnitsQuery.refetch()}
    >
      Retry
    </Button>
  ) : null;
  const dispenseUnitField = (
    <FormField
      form={form}
      type="select"
      name="dispenseUnitId"
      label="Dispense as"
      description={dispenseUnitDescription}
      options={dispenseUnitOptions}
      placeholder="Unit"
      triggerClassName="w-full"
      contentProps={{
        position: 'popper',
        align: 'end',
        sideOffset: 6,
        className:
          'max-h-[min(70vh,var(--radix-select-content-available-height))] overscroll-none [&_[data-slot=select-scroll-up-button]]:absolute [&_[data-slot=select-scroll-up-button]]:inset-x-0 [&_[data-slot=select-scroll-up-button]]:top-0 [&_[data-slot=select-scroll-up-button]]:z-10 [&_[data-slot=select-scroll-up-button]]:bg-popover [&_[data-slot=select-scroll-down-button]]:absolute [&_[data-slot=select-scroll-down-button]]:inset-x-0 [&_[data-slot=select-scroll-down-button]]:bottom-0 [&_[data-slot=select-scroll-down-button]]:z-10 [&_[data-slot=select-scroll-down-button]]:bg-popover',
      }}
      afterTrigger={dispenseUnitRetryButton}
    />
  );

  return (
    <div className="flex flex-col gap-5">
      {isCoded ? (
        <section className="rounded-[12px] border border-border/70 bg-surface-sunken p-4 shadow-sm dark:bg-background/55">
          <div className="text-xs font-medium text-muted-foreground">Medication</div>
          <div className="mt-2 grid grid-cols-1 items-end gap-3 @md:grid-cols-[minmax(0,1fr)_11rem]">
            <div className="min-w-0">
              <MedicationSelector
                form={form}
                lastAutoDispenseUnitIdRef={lastAutoDispenseUnitIdRef}
              />
            </div>
            <div className="min-w-0">{dispenseUnitField}</div>
          </div>
          {hasSelectedMedication ? (
            <>
              <div className="mt-3">
                <MedicationMetaBadges medication={selectedMedication} />
              </div>
              <div className="mt-4 border-t border-border/60 pt-4">
                <FormularyStatusBadge
                  formulary={formulary}
                  isLoading={isFormularyFetching && !formularyQuery.data}
                  hasResponse={formularyQuery.data !== undefined}
                />
                {isFormularyFetching && formularyQuery.data ? (
                  <span
                    className="mt-2 flex items-center gap-1 text-xs text-muted-foreground"
                    aria-live="polite"
                  >
                    <Loader2 className="size-3 animate-spin" aria-hidden />
                    Updating plan information…
                  </span>
                ) : null}
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {showAlternativesButton ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      radius="pill"
                      onClick={() => setAlternativesOpen(true)}
                      disabled={isSwitching}
                      className="border-foreground/20 bg-transparent shadow-none hover:bg-secondary/40 dark:bg-transparent dark:hover:bg-secondary/40"
                    >
                      Compare plan alternatives ({alternativeCount})
                    </Button>
                  ) : null}
                  {formularyQuery.data ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      radius="pill"
                      onClick={() => setDetailOpen(true)}
                      className="border-foreground/20 bg-transparent shadow-none hover:bg-secondary/40 dark:bg-transparent dark:hover:bg-secondary/40"
                    >
                      View plan details
                    </Button>
                  ) : null}
                  {onFormularyEligibilityOverrideChange ? (
                    <FormularyEligibilityPicker
                      eligibilities={eligibilitiesQuery.data?.items ?? []}
                      isLoading={eligibilitiesQuery.isLoading}
                      value={formularyEligibilityOverride}
                      onChange={onFormularyEligibilityOverrideChange}
                      resolvedEligibilityId={formularyQuery.data?.patientEligibilityId ?? null}
                    />
                  ) : null}
                </div>
              </div>
            </>
          ) : null}
          <PlanAlternativesDialog
            open={alternativesOpen}
            onOpenChange={setAlternativesOpen}
            display={display}
            payerInformation={formularyQuery.data?.payerInformation}
            isLoading={isFormularyFetching && !formularyQuery.data}
            onSelectAlternative={selectAlternative}
            isSelectingAlternative={isSwitching}
          />
          <FormularyDetailDialog
            open={detailOpen}
            onOpenChange={setDetailOpen}
            formularyStatus={formulary?.FormularyStatus}
            restrictions={formulary?.Restrictions}
            copays={formulary?.Copays}
            payerInformation={formularyQuery.data?.payerInformation}
          />
        </section>
      ) : (
        <>
          <FormField
            form={form}
            type="textarea"
            name="compoundDescription"
            label="Compound"
            placeholder="Describe the compound formulation"
            textAreaProps={{ className: 'min-h-[100px]' }}
          />
          <IngredientsEditor form={form} />
          {dispenseUnitField}
        </>
      )}

      <FormField
        form={form}
        type="textarea"
        name="directions"
        label="Directions"
        placeholder="e.g. Take 1 tablet by mouth daily"
        textAreaProps={{ className: 'min-h-[88px]' }}
      />

      <div className="grid grid-cols-1 gap-3 @md:grid-cols-3">
        <FormField form={form} type="text" name="quantity" label="Quantity" placeholder="e.g. 30" />
        <FormField form={form} type="text" name="refills" label="Refills" placeholder="e.g. 0" />
        <FormField
          form={form}
          type="text"
          name="daysSupply"
          label="Days supply"
          placeholder="e.g. 30"
        />
      </div>

      <FormField
        form={form}
        type="text"
        name="effectiveDate"
        label="Start date"
        placeholder="YYYY-MM-DD"
        description="Defaults to today. Change it when the prescription should begin later."
      />

      <PharmacySelector form={form} userId={userId} />

      <DiagnosisSelector
        form={form}
        requiredForInsuranceApproval={diagnosisRequiredForInsuranceApproval}
      />

      <details className="group rounded-[12px] border border-border/70 bg-surface-sunken shadow-sm dark:bg-background/55">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-medium">
          More prescribing options
          <ChevronDown
            className="size-4 text-muted-foreground transition-transform group-open:rotate-180"
            aria-hidden
          />
        </summary>
        <div className="space-y-4 border-t border-border/60 px-4 pb-4 pt-4">
          <FormField
            form={form}
            type="textarea"
            name="pharmacyNotes"
            label="Notes to pharmacy"
            placeholder="Optional"
            textAreaProps={{ className: 'min-h-[72px]' }}
          />
          <div className="grid grid-cols-1 gap-3 @md:grid-cols-2">
            <FormField
              form={form}
              type="checkbox"
              name="isUrgent"
              label="Mark as urgent"
              description="Ask the pharmacy to prioritize this prescription."
            />
            <FormField
              form={form}
              type="checkbox"
              name="noSubstitutions"
              label="Dispense as written"
              description="Do not allow equivalent substitutions."
            />
          </div>
        </div>
      </details>
    </div>
  );
}

interface FormularyEligibilityPickerProps {
  eligibilities: Array<{
    PatientEligibilityId?: number | null;
    PayerName?: string | null;
    PlanName?: string | null;
  }>;
  isLoading: boolean;
  value: number | null;
  onChange: (value: number | null) => void;
  /**
   * The eligibility id the server actually used on the most recent
   * formulary response. Surfaced as a hint when the picker is on
   * "Auto" so the clinician can see which coverage was selected.
   */
  resolvedEligibilityId: number | null;
}

/**
 * Optional override for the formulary lookup's ``patientEligibilityId``.
 *
 * Hidden until at least one coverage with a non-null eligibility id is
 * known — there's nothing to choose between otherwise. The default
 * "Auto" option preserves the original behaviour (server picks the
 * first active coverage that covers the channel).
 */
function FormularyEligibilityPicker({
  eligibilities,
  isLoading,
  value,
  onChange,
  resolvedEligibilityId,
}: FormularyEligibilityPickerProps) {
  const options = eligibilities.filter(
    (
      entry
    ): entry is {
      PatientEligibilityId: number;
      PayerName?: string | null;
      PlanName?: string | null;
    } => typeof entry.PatientEligibilityId === 'number'
  );

  if (isLoading || options.length < 2) {
    return null;
  }

  const selectValue =
    value !== null && options.some((entry) => entry.PatientEligibilityId === value)
      ? String(value)
      : FORMULARY_ELIGIBILITY_AUTO_VALUE;

  const handleChange = (next: string) => {
    if (next === FORMULARY_ELIGIBILITY_AUTO_VALUE) {
      onChange(null);
      return;
    }
    const parsed = Number(next);
    onChange(Number.isFinite(parsed) ? parsed : null);
  };

  const resolvedEligibility = options.find(
    (entry) => entry.PatientEligibilityId === resolvedEligibilityId
  );
  const autoPlanName = resolvedEligibility?.PlanName ?? resolvedEligibility?.PayerName;
  const autoLabel = autoPlanName ? `Auto-select: ${autoPlanName}` : 'Auto-select plan';

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground">Insurance plan</span>
      <Select value={selectValue} onValueChange={handleChange}>
        <SelectTrigger size="sm" className="h-8 max-w-[16rem] px-3 text-xs">
          <SelectValue placeholder="Auto-select plan" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={FORMULARY_ELIGIBILITY_AUTO_VALUE}>{autoLabel}</SelectItem>
          {options.map((entry) => {
            const id = entry.PatientEligibilityId;
            const label = entry.PayerName ?? entry.PlanName ?? `Coverage #${id}`;
            const detail = entry.PlanName && entry.PayerName ? entry.PlanName : null;
            return (
              <SelectItem key={id} value={String(id)}>
                <span className="flex items-baseline gap-2">
                  <span>{label}</span>
                  {detail ? (
                    <span className="text-[10px] text-muted-foreground">{detail}</span>
                  ) : null}
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}
